import { useConfig } from '@/composables/game/config'
import { computed, type ComputedRef, reactive, ref } from 'vue'
import type { CustomQuestTaskItem, DropeeMappedTaskItem } from '@/types/task'
import DailyCheckinImage from '@/assets/images/calendar.png'
import { ACTIVITY_ID, DAILY_TASK_ID } from '@/types/shared/player'
import DailyImproveImage from '@/assets/images/analytics.png'
import DailyTapImage from '@/assets/images/finger-up.png'
import DailyPokeImage from '@/assets/images/poke.png'
import DailyQuestionImage from '@/assets/images/question-mark-white.png'
import DailySpinImage from '@/assets/images/fortune-wheel.png'
import { useTranslate } from '@tolgee/vue'
import router from '@/router'
import { RouteName } from '@/routes'
import api from '@/services/api'
import { defaultToken } from '@/enums/token'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import type { PlayerTask } from '@/types/shared/player-config'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import { usePlayer } from '@/composables/game/player'
import { useFlyingCoins } from '@/composables/game/flying-coins'
import { useToaster } from '@/composables/game/toasters'
import {
  useDailyBuyStars,
  useDailyCrackTheEgg,
  useDailyImprove,
  useDailyPoke,
  useDailyPurchaseTask,
  useDailyQuestion,
  useDailyRadarTx,
  useDailySpin,
  useDailyTap,
  useDailyTx,
} from '@/composables/game/daily'
import { useAd } from '@/composables/game/ad'
import { taskManualVerification } from '@/config/task'
import { isiOS } from '@/helpers/browser'
import webapp from '@/services/webapp'
import { useNow } from '@/composables/game/time'
import { Duration } from 'luxon'
import { hideTonPayment } from '@/composables/init'

const shareStoryTaskId = 'shareToStory'
const watchAdTaskId = ACTIVITY_ID.WATCH_AD_FOR_SPIN
const dailyTransactionTaskIdLegacy = DAILY_TASK_ID.TON_DAILY_CHECKIN_LEGACY
const dailyTransactionTaskId = DAILY_TASK_ID.TON_DAILY_CHECKIN
const dailyRadarTransactionTaskId = DAILY_TASK_ID.DAPP_RADAR_TTM
const dailyRewardTaskId = 'daily_checkin'
const dailyQuestionTaskId = 'daily_question'
const refer3FriendsId = 'referral_3'
const refer5FriendsId = 'referral_5'
const refer10FriendsId = 'referral_10'
const tokensTaskId = 'choose_token'
const walletConnectTaskId = 'connect_ton_wallet'
const buyStarsTaskId = DAILY_TASK_ID.BUY_STARS
const dailyPurchaseTaskId = DAILY_TASK_ID.DAILY_PURCHASE
const addDropeeHomeScreenTaskId = 'add_dropee_homescreen'
const showBuyStarsModal = ref(false)

export const taskIdsWithoutManualClaim: string[] = [watchAdTaskId]
export const mysteryTaskAvailableDays = 7
export const mysteryTaskCategory = 'mystery'

export const useTaskList = () => {
  const { config } = useConfig()
  const { player, doTask, hasCheckedInToday, claimingTaskId, claimTask, updatePlayer } = usePlayer()
  const { startCoinAnimation } = useFlyingCoins()
  const { notify } = useToaster()
  const { t } = useTranslate()
  const { claimedDailyTap, dailyTapCount, maxTapCount } = useDailyTap()
  const { claimedDailyBuyStars, dailyStarsDeposit, maxStarsCount } = useDailyBuyStars()
  const { claimedDailyPurchaseTask, didDailyTaskPurchase, maxDailyPurchaseCount } = useDailyPurchaseTask()
  const { claimedDailyImprove, dailyImproveCount, maxImproveCount } = useDailyImprove()
  const { claimedDailyPoke, dailyPokeCount } = useDailyPoke()
  const { reward: dailyQuestionReward, claimed: claimedDailyQuestion } = useDailyQuestion()
  const { reward: dailySpinReward, claimed: claimedSpin, dailySpinCount } = useDailySpin()
  const { claimedDailyCrackTheEgg, hasCrackedToday } = useDailyCrackTheEgg()
  const { claimedDailyTx } = useDailyTx()
  const { claimedDailyRadar } = useDailyRadarTx()
  const isDailyTxClaimable = computed(() => player.value.tasks.tonDailyCheckinCredits > 0)
  const isDailyRadarTxClaimable = computed(() => player.value.tasks.dappRadarTtmCredits > 0)
  const { claimed: claimedWatchAds, adWatchedSpinReward } = useAd()

  const { open: openConnectTonModal } = useTonConnectModal()
  const walletTaskReward = computed(() => config.value.game.tasks.others.connectTonWallet.reward)
  const { addHapticFeedback } = useHapticFeedback()
  const { isTonWalletConnected } = useWalletConnect()

  const showDailyRewardModal = ref(false)
  const showDailyQuestionModal = ref(false)
  const showTaskModal = ref(false)
  const showStoryTaskModal = ref(false)
  const showWatchAdsModal = ref(false)
  const showTransactionModalLegacy = ref(false)
  const showTransactionModal = ref(false)
  const showRadarTransactionModal = ref(false)
  const showDailyPurchaseModal = ref(false)
  const showMysteryTaskSolvedOverlay = ref(false)
  const mysteryTaskForModal = ref<DropeeMappedTaskItem | null>(null)

  const selectedTaskItem = ref<PlayerTask | DropeeMappedTaskItem | null>(null)
  const isWalletTaskDone = computed(() => Boolean(player.value.tasks.others[walletConnectTaskId]?.isDone))
  const isTokenTaskDone = computed(() => Boolean(player.value.token))

  const checkedTokensPage = useLocalStorage<boolean>(buildDropeeStorageKey(`opened_task_url_${tokensTaskId}`), false)
  const shareStoryDone = useLocalStorage<boolean>(buildDropeeStorageKey(`opened_task_url_share_story`), false)
  const shareTaskReward = computed(() => getTaskRewardFromPlayerTaskItem(shareStoryTaskId))
  const isShareTaskClaimed = computed(() => isTaskDone(shareStoryTaskId))
  const taskError = reactive<Record<string, string>>({})

  const customPartnerSections = ['parking_jam', 'thief_puzzle', 'web_master_3d']

  const generalClickBasedTasks = computed(() => config.value.tasks.filter(task => !task.categories.includes('partners') && !task.categories.some(cat => customPartnerSections.includes(cat))))
  const partnerClickBasedTasks = computed(() => config.value.tasks.filter(task => task.categories.includes('partners') && !task.categories.some(cat => customPartnerSections.includes(cat))))
  const parkingJamPartnerClickBasedTasks = computed(() => config.value.tasks.filter(task => task.categories.includes('partners') && task.categories.includes('parking_jam')))
  const thiefPuzzlePartnerClickBasedTasks = computed(() => config.value.tasks.filter(task => task.categories.includes('partners') && task.categories.includes('thief_puzzle')))
  const webMaster3DPartnerClickBasedTasks = computed(() => config.value.tasks.filter(task => task.categories.includes('partners') && task.categories.includes('web_master_3d')))

  const partnerTasks = computed(() => partnerClickBasedTasks.value.map(task => (mapPlayerTask(task))))
  const orderedPartnerTasks = computed<DropeeMappedTaskItem[]>((previousValue) => {
    const newList = orderTasks(partnerTasks)
    return previousValue && JSON.stringify(newList) === JSON.stringify(previousValue) ? previousValue : newList
  })

  const parkingJamPartnerTasks = computed(() => parkingJamPartnerClickBasedTasks.value.map(task => (mapPlayerTask(task))))
  const orderedParkingJamPartnerTasks = computed<DropeeMappedTaskItem[]>((previousValue) => {
    const newList = orderTasks(parkingJamPartnerTasks)
    return previousValue && JSON.stringify(newList) === JSON.stringify(previousValue) ? previousValue : newList
  })

  const thiefPuzzlePartnerTasks = computed(() => thiefPuzzlePartnerClickBasedTasks.value.map(task => (mapPlayerTask(task))))
  const orderedThiefPuzzlePartnerTasks = computed<DropeeMappedTaskItem[]>((previousValue) => {
    const newList = orderTasks(thiefPuzzlePartnerTasks)
    return previousValue && JSON.stringify(newList) === JSON.stringify(previousValue) ? previousValue : newList
  })

  const webMaster3DPartnerTasks = computed(() => webMaster3DPartnerClickBasedTasks.value.map(task => (mapPlayerTask(task))))
  const orderedWebMaster3DPartnerTasks = computed<DropeeMappedTaskItem[]>((previousValue) => {
    const newList = orderTasks(webMaster3DPartnerTasks)
    return previousValue && JSON.stringify(newList) === JSON.stringify(previousValue) ? previousValue : newList
  })

  const referredFriendsCount = computed(() => player.value.referrals.count + player.value.friends.virtualCount)

  const addDropeeToHomeScreenTask = computed(() => generalClickBasedTasks.value.find(t => t.id === addDropeeHomeScreenTaskId))
  const isAddDropeeToHomeScreenDone = computed(() => addDropeeToHomeScreenTask.value?.isDone ?? false)

  const customTasksMap = computed((): Record<string, CustomQuestTaskItem> => {
    const configObj: Record<string, CustomQuestTaskItem> = {
      [dailyTransactionTaskIdLegacy]: {
        isDone: claimedDailyCrackTheEgg.value,
        progress: { done: Number(hasCrackedToday.value), max: 1 },
        hideProgress: true,
        spins: config.value.game.tasks.dailyTasks[DAILY_TASK_ID.TON_DAILY_CHECKIN_LEGACY].spins,
        reward: config.value.game.tasks.dailyTasks[DAILY_TASK_ID.TON_DAILY_CHECKIN_LEGACY].reward,
        onClick: () => {
          showTransactionModalLegacy.value = true
        },
        onClaim: () => claimTask(DAILY_TASK_ID.TON_DAILY_CHECKIN_LEGACY),
      },
      [walletConnectTaskId]: {
        reward: config.value.game.tasks.others.connectTonWallet.reward,
        isDone: isWalletTaskDone.value,
        progress: { done: isTonWalletConnected.value ? 1 : 0, max: 1 },
        hideProgress: true,
        onClick: walletTaskClicked,
        onClaim: doWalletTask,
      },
      [tokensTaskId]: {
        reward: config.value.game.tasks.others.setToken.reward,
        isDone: isTokenTaskDone.value,
        progress: { done: (checkedTokensPage || !!player.value.token) ? 1 : 0, max: 1 },
        hideProgress: true,
        onClick: tokensTaskClick,
        onClaim: doTokensTask,
      },
      [watchAdTaskId]: {
        isDone: claimedWatchAds.value,
        hideProgress: true,
        spins: adWatchedSpinReward.value,
        onClick: watchAdTaskClicked,
      },
      [shareStoryTaskId]: {
        reward: shareTaskReward.value,
        isDone: isShareTaskClaimed.value,
        progress: { done: +shareStoryDone.value, max: 1 },
        hideProgress: true,
        onClick: storyTaskClicked,
        onClaim: () => claimStoryTask(),
      },
      [refer3FriendsId]: {
        reward: getTaskRewardFromPlayerTaskItem(refer3FriendsId),
        isDone: isTaskDone(refer3FriendsId),
        progress: { done: referredFriendsCount.value, max: 3 },
        onClick: referFriendsClick,
        onClaim: () => _doTask(refer3FriendsId, getTaskRewardFromPlayerTaskItem(refer3FriendsId)),
      },
      [refer5FriendsId]: {
        reward: getTaskRewardFromPlayerTaskItem(refer5FriendsId),
        isDone: isTaskDone(refer5FriendsId),
        progress: { done: referredFriendsCount.value, max: 5 },
        onClick: referFriendsClick,
        onClaim: () => _doTask(refer5FriendsId, getTaskRewardFromPlayerTaskItem(refer5FriendsId)),
      },
      [refer10FriendsId]: {
        reward: getTaskRewardFromPlayerTaskItem(refer10FriendsId),
        spins: getTaskSpinsFromPlayerTaskItem(refer10FriendsId),
        isDone: isTaskDone(refer10FriendsId),
        progress: { done: referredFriendsCount.value, max: 10 },
        onClick: referFriendsClick,
        onClaim: () => _doTask(refer10FriendsId, getTaskRewardFromPlayerTaskItem(refer10FriendsId)),
      },
      [addDropeeHomeScreenTaskId]: {
        reward: addDropeeToHomeScreenTask.value?.reward,
        isDone: isAddDropeeToHomeScreenDone.value,
        hideProgress: true,
        onClick: setAddDropeToHomeScreenAsSelectedTask,
        onSecondaryClick: addDropeeToHomeScreenClick,
        onClaim: () => _doTask(addDropeeHomeScreenTaskId, addDropeeToHomeScreenTask.value?.reward),
      },
      [buyStarsTaskId]: {
        reward: config.value.game.tasks.dailyTasks[DAILY_TASK_ID.BUY_STARS].reward,
        spins: config.value.game.tasks.dailyTasks[DAILY_TASK_ID.BUY_STARS].spins,
        tickets: config.value.game.tasks.dailyTasks[DAILY_TASK_ID.BUY_STARS].tickets,
        hideProgress: true,
        isDone: claimedDailyBuyStars.value,
        progress: { done: dailyStarsDeposit.value, max: maxStarsCount.value },
        onClick: dailyBuyStarsClicked,
        onClaim: () => claimTask(DAILY_TASK_ID.BUY_STARS),
      },
      [dailyPurchaseTaskId]: {
        reward: getTaskRewardFromPlayerTaskItem(dailyPurchaseTaskId),
        spins: getTaskSpinsFromPlayerTaskItem(dailyPurchaseTaskId),
        tickets: config.value.game.tasks.dailyTasks[dailyPurchaseTaskId].tickets,
        hideProgress: true,
        isDone: claimedDailyPurchaseTask.value,
        progress: { done: didDailyTaskPurchase.value ? 1 : 0, max: maxDailyPurchaseCount.value },
        onClick: dailyPurchaseTaskClicked,
        onClaim: () => claimTask(DAILY_TASK_ID.DAILY_PURCHASE),
      },
      [mysteryTaskCategory]: {
        reward: getMysteryTaskReward,
        spins: getMysteryTaskSpins,
        hideProgress: true,
        progress: getMysteryTaskProgress,
        isDone: isMysteryTaskDone,
        onClick: mysteryTaskClick,
        onClaim: mysteryTaskClaim,
      },
      [dailyTransactionTaskId]: {
        isDone: claimedDailyTx.value,
        progress: { done: Number(isDailyTxClaimable.value), max: 1 },
        hideProgress: true,
        reward: getTaskRewardFromPlayerTaskItem(dailyTransactionTaskId),
        spins: getTaskSpinsFromPlayerTaskItem(dailyTransactionTaskId),
        tickets: config.value.game.tasks.dailyTasks[dailyTransactionTaskId].tickets,
        hidden: hideTonPayment.value,
        onClick: () => {
          showTransactionModal.value = true
        },
        onClaim: async () => {
          const successful = await claimTask(DAILY_TASK_ID.TON_DAILY_CHECKIN)
          if (!successful) {
            notify({ type: 'error', title: 'Incomplete. Try again' })
          }
        },
      },
      [dailyRadarTransactionTaskId]: {
        isDone: claimedDailyRadar.value,
        progress: { done: Number(isDailyRadarTxClaimable.value), max: 1 },
        hideProgress: true,
        reward: getTaskRewardFromPlayerTaskItem(dailyRadarTransactionTaskId),
        spins: getTaskSpinsFromPlayerTaskItem(dailyRadarTransactionTaskId),
        tickets: config.value.game.tasks.dailyTasks[dailyRadarTransactionTaskId].tickets,
        hidden: hideTonPayment.value,
        onClick: () => {
          showRadarTransactionModal.value = true
        },
        onClaim: async () => {
          const successful = await claimTask(DAILY_TASK_ID.DAPP_RADAR_TTM)
          if (!successful) {
            notify({ type: 'error', title: 'Incomplete. Try again' })
          }
        },
      },
    }
    return configObj
  })

  const { isMysteryTaskAvailable } = useMysteryTask()

  const generalTasks = computed((): DropeeMappedTaskItem[] => {
    // Touch `customTasksMap.value` properties reactively
    const customTasks = customTasksMap.value

    return generalClickBasedTasks.value
      .filter((task) => {
        const isCustom = task.categories.includes('custom')
        const isMystery =
          task.categories.includes(mysteryTaskCategory) &&
          isMysteryTaskAvailable(task.startsOn)

        // Explicitly check properties reactively
        const customTaskExists = !!customTasks[task.id as keyof typeof customTasks]
        const mysteryTaskExists = !!customTasks[mysteryTaskCategory]

        return (
          ((isCustom && (customTaskExists || (isMystery && mysteryTaskExists))) || !isCustom) && !customTasks[task.id]?.hidden
        )
      })
      .map((task) => mapPlayerTask(task))
  })

  const orderedGeneralTasks = computed<DropeeMappedTaskItem[]>((previousValue) => {
    const newList = orderTasks(generalTasks)
    return previousValue && JSON.stringify(newList) === JSON.stringify(previousValue) ? previousValue : newList
  })

  const dailyTasks = computed((): DropeeMappedTaskItem[] => {
    return [
      {
        id: dailyRewardTaskId,
        title: t.value('quests.daily_check_in.title', 'Daily check-in'),
        reward: dailyRewardTotal.value,
        imageUrl: DailyCheckinImage,
        isDone: hasCheckedInToday.value,
        onClick: dailyRewardTaskClicked,
      },
      {
        id: DAILY_TASK_ID.UPGRADE,
        title: t.value('quests.daily_improve.title', 'Improve {count} cards', { count: config.value.game.tasks.dailyTasks.upgrade.required }),
        reward: config.value.game.tasks.dailyTasks.upgrade.reward,
        imageUrl: DailyImproveImage,
        isDone: claimedDailyImprove.value,
        progress: { done: dailyImproveCount.value, max: maxImproveCount.value },
        onClick: dailyImproveClick,
        onClaim: () => claimTask(DAILY_TASK_ID.UPGRADE),
      },
      {
        id: DAILY_TASK_ID.TAP,
        title: t.value('quests.daily_tap.title', 'Tap for {count} points', { count: config.value.game.tasks.dailyTasks.tap.required }),
        reward: config.value.game.tasks.dailyTasks.tap.reward,
        imageUrl: DailyTapImage,
        isDone: claimedDailyTap.value,
        progress: { done: dailyTapCount.value, max: maxTapCount.value },
        onClick: dailyTapClick,
        onClaim: () => claimTask(DAILY_TASK_ID.TAP),
      },
      {
        id: DAILY_TASK_ID.POKE,
        title: t.value('quests.daily_poke.title', 'Poke 1 friend'),
        reward: config.value.game.tasks.dailyTasks.poke.reward,
        imageUrl: DailyPokeImage,
        isDone: claimedDailyPoke.value,
        progress: { done: dailyPokeCount.value, max: 1 },
        hideProgress: true,
        onClick: dailyPokeClick,
        onClaim: () => claimTask(DAILY_TASK_ID.POKE),
      },
      {
        id: dailyQuestionTaskId,
        title: t.value('quests.daily_question.title', 'Question of the day'),
        reward: dailyQuestionReward.value,
        imageUrl: DailyQuestionImage,
        isDone: claimedDailyQuestion.value,
        onClick: dailyQuestionTaskClicked,
      },
      {
        id: DAILY_TASK_ID.SPIN_THE_WHEEL,
        title: t.value('quests.spin_wheel.title', 'Spin the wheel'),
        reward: dailySpinReward.value,
        imageUrl: DailySpinImage,
        isDone: claimedSpin.value,
        progress: { done: dailySpinCount.value, max: 1 },
        hideProgress: true,
        onClick: spinWheelClicked,
        onClaim: () => claimTask(DAILY_TASK_ID.SPIN_THE_WHEEL),
      },
    ]
  })

  const orderedDailyTasks = computed<DropeeMappedTaskItem[]>((previousValue) => {
    const newList = orderTasks(dailyTasks)
    return previousValue && JSON.stringify(newList) === JSON.stringify(previousValue) ? previousValue : newList
  })

  const partnerSectionsMap = computed(() => {
    return {
      partners: {
        items: orderedPartnerTasks,
        title: 'Partners',
        description: 'Complete partner tasks to earn exclusive bonuses.',
      },
      parking_jam: {
        items: orderedParkingJamPartnerTasks,
        title: 'Color Water Sort',
        description: 'Play our partner\'s game and claim exclusive rewards!',
      },
      thief_puzzle: {
        items: orderedThiefPuzzlePartnerTasks,
        title: 'Thief Puzzle',
        description: 'Play our partner\'s game and claim exclusive rewards!',
      },
      web_master_3d: {
        items: orderedWebMaster3DPartnerTasks,
        title: 'Web Master 3D',
        description: 'Play our partner\'s game and claim exclusive rewards!',
      },
    }
  })

  const undoneTasks = computed(() => {
    return [
      ...dailyTasks.value,
      ...generalTasks.value,
      ...partnerTasks.value,
      ...parkingJamPartnerTasks.value,
      ...thiefPuzzlePartnerTasks.value,
      ...webMaster3DPartnerTasks.value,
    ].filter(task => !task.isDone && !task.hasManualVerification)
  })

  function mapPlayerTask(task: PlayerTask): DropeeMappedTaskItem {
    const isCustom = task.categories.includes('custom')
    const isMystery = task.categories.includes(mysteryTaskCategory)
    const customTaskConfig = isCustom ? customTasksMap.value[(isMystery ? mysteryTaskCategory : task.id) as keyof typeof customTasksMap.value] : undefined

    return {
      id: task.id,
      title: task.title,
      description: task.description,
      imageUrl: task.imageUrl,
      url: task.url,
      reward: customTaskConfig ? (typeof customTaskConfig.reward === 'function' ? customTaskConfig.reward(task) : customTaskConfig.reward) : task.reward,
      spins: customTaskConfig ? (typeof customTaskConfig.spins === 'function' ? customTaskConfig.spins(task) : customTaskConfig.spins) : task.spins,
      tickets: customTaskConfig ? (typeof customTaskConfig.tickets === 'function' ? customTaskConfig.tickets(task) : customTaskConfig.tickets) ?? task.tickets : task.tickets,
      isDone: customTaskConfig ? (typeof customTaskConfig.isDone === 'function' ? customTaskConfig.isDone(task) : customTaskConfig.isDone) : task.isDone,
      progress: typeof customTaskConfig?.progress === 'function' ? customTaskConfig.progress(task) : customTaskConfig?.progress,
      hideProgress: customTaskConfig?.hideProgress ?? false,
      actionCompletedAt: task.actionCompletedAt,
      claimDelay: task.claimDelay,
      hasManualVerification: task.validations.includes(taskManualVerification),
      isVerified: task.isVerified,
      cta: task.cta,
      ctaAfterDone: task.ctaAfterDone,
      categories: task.categories,
      startsOn: task.startsOn,
      onClick: customTaskConfig ? () => customTaskConfig.onClick(task) : () => setSelectedTaskItem(task),
      onSecondaryClick: customTaskConfig ? customTaskConfig.onSecondaryClick : undefined,
      onClaim: customTaskConfig && customTaskConfig.onClaim
        ? () => customTaskConfig.onClaim!(task)
        : () => _doTask(task.id, task.reward),
    }
  }

  function dailyRewardTaskClicked() {
    router.push({ name: RouteName.DAILY_REWARDS })
  }

  function dailyQuestionTaskClicked() {
    showDailyQuestionModal.value = true
  }

  async function setSelectedTaskItem(task: PlayerTask | undefined) {
    if (!task) return
    // if (task.targetedCountry) {
    //   const location = await webapp.requestLocation()
    //   if (location) {
    //
    //   }
    // }
    selectedTaskItem.value = getPlayerTaskById(task.id)
  }

  function setAddDropeToHomeScreenAsSelectedTask() {
    const mappedTask = generalTasks.value.find(t => t.id === addDropeeHomeScreenTaskId)
    if (!mappedTask) return
    selectedTaskItem.value = mappedTask
  }

  function getPlayerTaskById(id: string) {
    return config.value.tasks.find(task => task.id === id) ?? null
  }

  function orderTasks(taskList: ComputedRef<DropeeMappedTaskItem[]>) {
    const doneList = taskList.value.filter(task => task.isDone)
    const undoneList = taskList.value.filter(task => !doneList.includes(task))
    return [...undoneList, ...doneList]
  }

  function isTaskDone(id: string) {
    return Boolean(player.value.tasks.others[id]?.isDone)
  }

  function getTaskRewardFromPlayerTaskItem(id: string) {
    const task = getPlayerTaskById(id)
    if (!task) {
      console.error('No task found in tasks list')
      return 0
    }
    return task.reward
  }

  function getTaskSpinsFromPlayerTaskItem(id: string) {
    const task = getPlayerTaskById(id)
    if (!task) {
      console.error('No task found in tasks list')
      return 0
    }
    return task.spins
  }

  function getTaskRewardFromConfig(key: string) {
    const configKey = key as keyof typeof config.value.game.tasks.others
    const task = config.value.game.tasks.others[configKey]
    if (!task) {
      console.error('No task found in reward config')
      return 0
    }
    return config.value.game.tasks.others[configKey].reward
  }

  const dailyRewardTotal = computed(() => config.value.game.tasks.dailyCheckin.rewards.reduce((partialSum, a) => partialSum + a, 0))

  function closeTaskModal() {
    showTaskModal.value = false
    setTimeout(() => selectedTaskItem.value = null, 100)
  }

  async function _doTask(taskId: string, taskReward?: number) {
    taskError[taskId] = ''

    claimingTaskId.value = taskId
    try {
      const { success, error } = await doTask(taskId, taskReward ?? 0)
      if (success && !error) {
        addHapticFeedback()
        startCoinAnimation()
        notify({ title: t.value('task.completed', 'Success!'), type: 'success' })
        closeTaskModal()
        return true
      } else {
        notify({ title: t.value('task.not_completed', `Task isn't completed`), type: 'error' })
      }
      if (error) {
        taskError[taskId] = t.value('task.error.message', 'You didn’t finish the task. Please try again.')
      }
      return false
    } finally {
      claimingTaskId.value = null
    }
  }

  function dailyTapClick() {
    router.push({ name: RouteName.HOME })
  }

  function dailyImproveClick() {
    router.push({ name: RouteName.MINE })
  }

  function dailyPokeClick() {
    router.push({ name: RouteName.FRIENDS })
  }

  async function doWalletTask() {
    if (isWalletTaskDone.value) {
      await router.push({ name: RouteName.PROFILE })
      return
    }
    claimingTaskId.value = walletConnectTaskId
    try {
      await _doTask(walletConnectTaskId, walletTaskReward.value)
    } finally {
      claimingTaskId.value = null
    }
  }

  async function walletTaskClicked() {
    if (isWalletTaskDone.value) {
      await router.push({ name: RouteName.PROFILE })
      return
    }
    if (isTonWalletConnected.value) {
      await doWalletTask()
      return
    }
    openConnectTonModal()
  }

  function referFriendsClick() {
    router.push({ name: RouteName.FRIENDS })
  }

  function storyTaskClicked() {
    showStoryTaskModal.value = true
  }

  function watchAdTaskClicked() {
    showWatchAdsModal.value = true
  }

  function dailyBuyStarsClicked() {
    showBuyStarsModal.value = true
  }

  async function dailyPurchaseTaskClicked() {
    showDailyPurchaseModal.value = true
  }

  function tokensTaskClick() {
    checkedTokensPage.value = true
    router.push({ name: RouteName.TOKENS })
  }

  function spinWheelClicked() {
    router.push({ name: RouteName.REWARDS })
  }

  function addDropeeToHomeScreenClick() {
    console.log('addDropeeToHomeScreenClick')
    webapp.addToHomeScreen()
  }

  async function doTokensTask() {
    claimingTaskId.value = tokensTaskId
    try {
      const { playerStats } = await api.setToken(defaultToken)
      startCoinAnimation()
      updatePlayer(playerStats)
    } finally {
      claimingTaskId.value = null
    }
  }

  function getMysteryTaskReward(task: PlayerTask) {
    return task.reward
  }

  function getMysteryTaskSpins(task: PlayerTask) {
    return task.spins
  }

  function getMysteryTaskProgress(task: PlayerTask) {
    return { done: task.actionCompletedAt ? 1 : 0, max: 1 }
  }

  function isMysteryTaskDone(task: PlayerTask) {
    return task.isDone
  }

  function mysteryTaskClick(task?: PlayerTask) {
    if (!task || task.isDone) return
    mysteryTaskForModal.value = generalTasks.value.find(t => t.id === task.id) ?? null
  }

  async function mysteryTaskClaim(task?: PlayerTask) {
    if (!task || task.isDone) return
    const success = await _doTask(task.id, getMysteryTaskReward(task))
    if (success) {
      mysteryTaskForModal.value = null
      setTimeout(() => showMysteryTaskSolvedOverlay.value = true, 200)
    }
  }

  async function claimStoryTask() {
    await _doTask(shareStoryTaskId, getTaskRewardFromPlayerTaskItem(shareStoryTaskId))
  }

  return {
    orderedPartnerTasks,
    orderedParkingJamPartnerTasks,
    orderedThiefPuzzlePartnerTasks,
    orderedWebMaster3DPartnerTasks,
    orderedGeneralTasks,
    orderedDailyTasks,
    selectedTaskItem,
    showDailyRewardModal,
    showDailyQuestionModal,
    showTaskModal,
    showStoryTaskModal,
    showWatchAdsModal,
    showTransactionModalLegacy: showTransactionModalLegacy,
    showTransactionModal,
    showRadarTransactionModal,
    dailyTransactionTaskId: dailyTransactionTaskIdLegacy,
    undoneTasks,
    _doTask,
    claimingTaskId,
    taskError,
    showBuyStarsModal,
    showDailyPurchaseModal,
    watchAdTaskId,
    buyStarsTaskId,
    customTasksMap,
    generalClickBasedTasks,
    partnerClickBasedTasks,
    claimTask,
    mysteryTaskForModal,
    showMysteryTaskSolvedOverlay,
    shareStoryDone,
    claimStoryTask,
    shareTaskReward,
    isShareTaskClaimed,
    dailyPurchaseTaskId,
    partnerSectionsMap,
  }
}

export const useDailyTransactionTask = () => {
  const isIOSBrowser = isiOS()
  const iosLink = 'https://apps.apple.com/ro/app/tonkeeper-ton-wallet/id1587742107' // iOS App Store link
  const androidLink = 'https://play.google.com/store/apps/details?id=com.ton_keeper' // Google Play Store link

  function openTonkeeperInStore() {
    const appLink = isIOSBrowser ? iosLink : androidLink
    window.open(appLink, '_blank')
  }

  return {
    openTonkeeperInStore,
  }
}

export const useMysteryTask = () => {
  const { now } = useNow()

  function getEndSeconds(startsOn: number) {
    return Duration.fromObject({ seconds: startsOn }).plus({ days: mysteryTaskAvailableDays }).as('seconds')
  }

  function isMysteryTaskAvailable(startsOn: number | null) {
    if (!startsOn) return false
    return startsOn <= now.value && getEndSeconds(startsOn) >= now.value
  }

  function getCountDownSeconds(startsOn: number | null) {
    if (!startsOn) return 0
    return Math.max(0, getEndSeconds(startsOn) - now.value)
  }

  return {
    isMysteryTaskAvailable,
    getCountDownSeconds,
  }
}